import { useEffect, useRef, useState } from 'preact/hooks';
import { useDebouncedCallback } from 'use-debounce';
import { updatePodcast } from '../../global-state/podcasts';
import {
  followPodcast,
  unfollowPodcast,
  useUserDataStore,
} from '../../global-state/user-data';
import { PodcastOnly } from '../../models/podcasts';
import {
  fetchSearchResults,
  getCachedSearchResults,
  getLastInputText,
  setLastInputText,
} from '../../utils/fetch-search-results';
import getThumbUrl from '../../utils/get-thumb';
import { classNames } from '../../utils/helpers';
import useMediaQuery from '../../utils/use-media-query';
import { Icon } from '../icon';
import { Link } from '../link';
import style from './style.module.scss';

const SearchPodcasts = () => {
  const inputRef = useRef<HTMLInputElement>(null);
  const queryRef = useRef('');
  const [suggestions, setSuggestions] = useState<PodcastOnly[]>([]);
  const [isLoading, setLoading] = useState(false);
  const [searchError, setError] = useState<Error>();
  const followedPodcastIds = useUserDataStore((store) => store.followedPodcastIds);
  const showFollowText = useMediaQuery('(min-width: 400px)');
  const debouncedSearch = useDebouncedCallback((query) => {
    queryRef.current = query;
    // console.log(query, 'FETCHING');
    fetchSearchResults(query).then(
      (podcasts) => {
        if (queryRef.current === query) {
          setSuggestions(podcasts);
          setLoading(false);
          queryRef.current = '';
        }
      },
      (error) => {
        if (queryRef.current === query) {
          setError(error as Error);
          setLoading(false);
          queryRef.current = '';
        }
      }
    );
  }, 100);

  const onInputChange = () => {
    const input = inputRef.current;
    if (!input) return;

    const text = input.value.trim();
    setLastInputText(text);
    setError(undefined);

    // Clear results
    if (text.length <= 1) {
      setLoading(false);
      setSuggestions([]);
      queryRef.current = '';
      return;
    }

    // No changes
    if (text === queryRef.current) return;

    // Attempt to display cached result (immediately)
    const cachedSuggestions = getCachedSearchResults(text);
    if (cachedSuggestions) {
      setSuggestions(cachedSuggestions);
      // console.log(text, 'CACHE');
      return;
    }

    // Wait for search results to load
    setLoading(true);
    setSuggestions([]);
    debouncedSearch(text);
    // console.log(text, 'LOADING', queryRef.current);
  };

  useEffect(() => {
    if (!inputRef.current) return;

    const priorValue = getLastInputText();
    if (priorValue) {
      // Update input value to match prior value
      inputRef.current.value = priorValue;
      setTimeout(onInputChange, 0);
    } else {
      // Focus empty input
      inputRef.current.focus();
    }
  }, []);

  const inputHasValue = Boolean(inputRef.current && inputRef.current.value);
  const searchHasNoResults = Boolean(
    !isLoading && !searchError && !suggestions.length && inputHasValue
  );

  const errorMessage = searchError
    ? `Error: ${searchError || 'No results'}`
    : searchHasNoResults
      ? 'No results'
      : '';

  return (
    <>
      <form
        class={style.searchInputForm}
        onSubmit={(event) => {
          event.preventDefault();
          onInputChange();
        }}
      >
        <input
          class={style.searchInput}
          onKeyUp={onInputChange}
          onChange={onInputChange}
          ref={inputRef}
        />
        {inputHasValue ? (
          <button
            class={classNames('button', style.clearButton)}
            type="reset"
            onClick={() => setTimeout(onInputChange, 0)}
          >
            <Icon sprite="close-circle" />
          </button>
        ) : (
          ''
        )}
      </form>
      {isLoading ? <div>Loading...</div> : ''}
      {errorMessage ? <div>{errorMessage}</div> : ''}
      {suggestions.map(SearchResultItemTemplate)}
    </>
  );

  function SearchResultItemTemplate(podcast: PodcastOnly, index: number) {
    const onAddFavorite = (event: Event) => {
      event.preventDefault(); // Prevent navigation
      event.stopPropagation(); // Prevent onClick handler on parent from being called
      updatePodcast(podcast, 'API');
      followPodcast(podcast);
    };
    const onRemoveFavorite = (event: Event) => {
      event.preventDefault(); // Prevent navigation
      event.stopPropagation(); // Prevent onClick handler on parent from being called
      unfollowPodcast(podcast);
    };
    return (
      <Link
        class={style.searchResultsItem}
        onClick={() => {
          updatePodcast(podcast, 'API');
        }}
        tabIndex={0}
        onKeyUp={(event) => {
          if (event.code === 'Enter') {
            updatePodcast(podcast, 'API');
          }
        }}
        style={index === 0 ? {} : { borderTop: '1px solid #e7e7e7' }}
        href={`/podcast/${podcast.id}`}
      >
        <img src={getThumbUrl(podcast, 100)} alt="" width="100" height="100" />
        <div style={{ flexGrow: 1 }}>
          {!followedPodcastIds.includes(podcast.id) ? (
            <button
              onClick={onAddFavorite}
              class="button button--favorite button--right-align-for-search"
              style={{
                float: 'right',
                marginLeft: '6px',
                marginTop: showFollowText ? '0' : '-4px',
              }}
            >
              <span>Follow</span>
              <Icon sprite="plus-circle" />
            </button>
          ) : (
            <button
              onClick={onRemoveFavorite}
              class="button button--favorite button--right-align-for-search"
              style={{
                float: 'right',
                marginLeft: '6px',
                marginTop: showFollowText ? '0' : '-4px',
              }}
            >
              <span>Following</span>
              <Icon class="text-green-500" sprite="checkmark-circle" />
            </button>
          )}

          <h3>{podcast.name}</h3>
          <div>{podcast.artistName}</div>
          <div>
            {podcast.episodeCount} {podcast.episodeCount === 1 ? 'Episode' : 'Episodes'}
          </div>
        </div>
      </Link>
    );
  }
};

export default SearchPodcasts;
